import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { MatchTimeResponse, OddModel, OddsMessage, OddsResponse } from 'models';
import { uniqueId } from 'utils/commonUtils';

type GameOddsState = {
  [fixtureId: string]: {
    [marketTypeId: string]: OddModel[];
  };
};

type GameMatchTime = {
  [fixtureId: string]: MatchTimeResponse;
};

type GameOddsStatus = {
  [oddId: string]: string;
};

export interface GameState {
  updateResult: string;
  gameOdds: GameOddsState;
  gameOddsStatus: GameOddsStatus;
  gameMatchTime: GameMatchTime;
}

const initialState: GameState = {
  updateResult: '',
  gameOdds: {},
  gameOddsStatus: {},
  gameMatchTime: {},
};

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    updateResult: (state) => {
      state.updateResult = uniqueId();
    },
    setGameOdds: (state, action) => {
      state.gameOdds = action.payload;
    },
    updateGameOddsResponse: (state, action: PayloadAction<GameOddsState>) => {
      const newGameOdds = action.payload;

      Object.entries(newGameOdds).forEach(([gameId, markets]) => {
        if (!state.gameOdds[gameId]) {
          state.gameOdds[gameId] = {};
        }

        Object.entries(markets).forEach(([market, odds]) => {
          state.gameOdds[gameId][market] = odds;
        });
      });
    },
    updateGameOdds: (state, action: PayloadAction<OddsMessage>) => {
      const { gameId, gameMarket, odds } = action.payload;

      if (!state.gameOdds[gameId]) {
        state.gameOdds[gameId] = {};
      }

      const oldOdds = state.gameOdds[gameId][gameMarket] || [];
      odds.forEach((newOdd) => {
        const oldOdd = oldOdds.find((odd) => odd.id === newOdd.id);
        if (oldOdd) {
          if (newOdd.decimalPrice > oldOdd.decimalPrice) {
            state.gameOddsStatus[newOdd.id] = 'increase';
          } else if (newOdd.decimalPrice < oldOdd.decimalPrice) {
            state.gameOddsStatus[newOdd.id] = 'decrease';
          } else {
            state.gameOddsStatus[newOdd.id] = 'none';
          }
        } else {
          state.gameOddsStatus[newOdd.id] = 'none';
        }
      });

      state.gameOdds[gameId][gameMarket] = odds;
    },
    clearAllOddsStatus: (state) => {
      state.gameOddsStatus = {};
    },
    clearOddsStatusById: (state, action: PayloadAction<string>) => {
      state.gameOddsStatus[action.payload] = 'none';
    },
    updateMatchTime: (state, action: PayloadAction<MatchTimeResponse>) => {
      const { fixtureId } = action.payload;
      state.gameMatchTime[fixtureId] = action.payload;
    },
  },
});

export const {
  updateResult,
  setGameOdds,
  updateGameOdds,
  updateGameOddsResponse,
  clearAllOddsStatus,
  clearOddsStatusById,
  updateMatchTime,
} = gameSlice.actions;

export const seleteUpdateGameResult = (state: RootState) => state.game.updateResult;
export const selectGameOdds = (state: RootState) => state.game.gameOdds;
export const selectGameOddsById = (state: RootState, gameId: string) => {
  return state.game.gameOdds[gameId] || {};
};

export const makeSelectGameOddsById = (gameId: string) =>
  createSelector(
    (state: RootState) => state.game,
    (game) => game.gameOdds[gameId] || [],
  );

export const selectMatchTimeById = (state: RootState, gameId: string) => {
  return state.game.gameMatchTime[gameId] || null;
};
export const selectOddStatus = (state: RootState, oddId: string) => {
  return state.game.gameOddsStatus[oddId];
};
// NOTE: Reducer
export default gameSlice.reducer;

export const transformOddsResponseToGameOddsState = (responses: OddsResponse[]): GameOddsState => {
  const result: GameOddsState = {};

  for (const { gameId, gameOdds } of responses) {
    if (!result[gameId]) {
      result[gameId] = {};
    }

    for (const odd of gameOdds) {
      const marketTypeId = odd.market;
      if (!result[gameId][marketTypeId]) {
        result[gameId][marketTypeId] = [];
      }
      result[gameId][marketTypeId].push(odd);
    }
  }

  return result;
};

export const convertOddsMessagesToGameOddsState = (message: OddsMessage): GameOddsState => {
  const result: GameOddsState = {};
  const { gameId, gameMarket, odds } = message;
  if (!result[gameId]) {
    result[gameId] = {};
  }
  result[gameId][gameMarket] = odds;
  return result;
};
